/**
 * My Calendar Feeds — owner-scoped CRUD page for ICS subscription feeds.
 *
 * The user creates one or more saved feeds, each with optional district
 * and day-type filters. Each feed exposes a public token-gated URL for
 * calendar clients to subscribe to. Token rotation invalidates the prior
 * URL.
 *
 * Gated by the calendar.pageEnabled feature flag.
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Modal, Label, TextInput } from 'flowbite-react';
import { HiCalendar, HiClipboard, HiPlus, HiRefresh, HiTrash } from 'react-icons/hi';

import {
  calendarFeedsApi,
  leasApi,
  getUserErrorMessage,
  type UserCalendarFeed,
} from '../api';
import { descriptorsApi, type Descriptor } from '../api/descriptors';
import type { LEAListItem } from '../types/api';
import {
  SearchableMultiSelect,
  type MultiSelectOption,
} from '../components/common/SearchableMultiSelect';
import { Loading } from '../components/common/Loading';
import { PageHeader } from '../components/common/PageHeader';
import { BOUND_DAY_TYPE_OPTIONS } from '../components/calendar/CalendarMonthGrid';
import { useAppSettings, APP_SETTING_KEYS } from '../contexts/AppSettingsContext';

interface FeedFormState {
  name: string;
  districtFilter: number[];
  dayTypeFilter: number[];
}

const EMPTY_FORM: FeedFormState = {
  name: '',
  districtFilter: [],
  dayTypeFilter: [],
};

export const MyCalendarFeedsPage = () => {
  const { getBool } = useAppSettings();
  const pageEnabled = getBool(APP_SETTING_KEYS.calendarPageEnabled, false);

  const [feeds, setFeeds] = useState<UserCalendarFeed[]>([]);
  const [districts, setDistricts] = useState<LEAListItem[]>([]);
  const [specialDayTypes, setSpecialDayTypes] = useState<Descriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFeed, setEditingFeed] = useState<UserCalendarFeed | null>(null);
  const [form, setForm] = useState<FeedFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (!pageEnabled) return;
    try {
      const [list, leas, types] = await Promise.all([
        calendarFeedsApi.list(),
        leasApi.list({ limit: 500 }),
        descriptorsApi.getByTypeName('Special Day Type'),
      ]);
      setFeeds(list);
      setDistricts(leas);
      setSpecialDayTypes(
        [...types]
          .filter((t) => t.active)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      );
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageEnabled]);

  const districtOptions: MultiSelectOption[] = useMemo(
    () =>
      districts.map((d) => ({
        id: d.educationOrganizationId,
        label: d.shortNameOfInstitution || d.nameOfInstitution,
      })),
    [districts]
  );

  const typeOptions: MultiSelectOption[] = useMemo(
    () => [
      ...specialDayTypes.map((t) => ({ id: t.descriptorId, label: t.shortDescription })),
      // Synthetic bound-day markers can be included in a saved feed too.
      ...BOUND_DAY_TYPE_OPTIONS.map((b) => ({ id: b.id, label: b.label })),
    ],
    [specialDayTypes]
  );

  const districtNameLookup = useMemo(() => {
    const m = new Map<number, string>();
    for (const d of districts) {
      m.set(d.educationOrganizationId, d.shortNameOfInstitution || d.nameOfInstitution);
    }
    return m;
  }, [districts]);

  const typeNameLookup = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of specialDayTypes) m.set(t.descriptorId, t.shortDescription);
    // So a saved feed's summary shows "First Day (Students)" not "#-101".
    for (const b of BOUND_DAY_TYPE_OPTIONS) m.set(b.id, b.label);
    return m;
  }, [specialDayTypes]);

  const flashSuccess = (msg: string) => {
    setSuccessMessage(msg);
    window.setTimeout(() => setSuccessMessage(null), 4000);
  };

  const openCreate = () => {
    setEditingFeed(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (feed: UserCalendarFeed) => {
    setEditingFeed(feed);
    setForm({
      name: feed.name,
      districtFilter: feed.districtFilter ?? [],
      dayTypeFilter: feed.dayTypeFilter ?? [],
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Feed name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        districtFilter: form.districtFilter.length ? form.districtFilter : null,
        dayTypeFilter: form.dayTypeFilter.length ? form.dayTypeFilter : null,
      };
      if (editingFeed) {
        await calendarFeedsApi.update(editingFeed.userCalendarFeedId, payload);
        flashSuccess(`Updated "${payload.name}".`);
      } else {
        await calendarFeedsApi.create(payload);
        flashSuccess(`Created "${payload.name}".`);
      }
      setModalOpen(false);
      await refresh();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (feed: UserCalendarFeed) => {
    if (!window.confirm(`Delete calendar feed "${feed.name}"? This invalidates the subscription URL.`)) {
      return;
    }
    try {
      await calendarFeedsApi.delete(feed.userCalendarFeedId);
      flashSuccess(`Deleted "${feed.name}".`);
      await refresh();
    } catch (err) {
      setError(getUserErrorMessage(err));
    }
  };

  const handleRotateToken = async (feed: UserCalendarFeed) => {
    if (
      !window.confirm(
        `Regenerate the URL for "${feed.name}"? The current subscription URL will stop working.`
      )
    ) {
      return;
    }
    try {
      await calendarFeedsApi.rotateToken(feed.userCalendarFeedId);
      flashSuccess(`Regenerated URL for "${feed.name}".`);
      await refresh();
    } catch (err) {
      setError(getUserErrorMessage(err));
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flashSuccess('Subscription URL copied to clipboard.');
    } catch {
      setError('Could not copy to clipboard. Select and copy manually.');
    }
  };

  if (!pageEnabled) {
    return (
      <div className="container mx-auto p-4">
        <PageHeader title="My Calendar Feeds" />
        <Alert color="info">
          Calendar feeds are not yet enabled. A super admin can turn them on
          from the App Settings page (key
          <code className="mx-1">calendar.pageEnabled</code>).
        </Alert>
      </div>
    );
  }

  if (loading) return <Loading />;

  return (
    <div className="container mx-auto p-4 space-y-4">
      <PageHeader
        title="My Calendar Feeds"
        subtitle="Subscribe to a filtered view of district calendars from Google Calendar, Outlook, or Apple Calendar via an iCalendar (.ics) URL."
        actions={
          <Button onClick={openCreate}>
            <HiPlus className="mr-2 h-4 w-4" />
            New Feed
          </Button>
        }
      />

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {feeds.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <HiCalendar className="mx-auto h-10 w-10 text-gray-300 mb-2" />
            <p className="font-medium text-gray-700 dark:text-gray-300">
              You have no saved calendar feeds yet.
            </p>
            <p className="text-sm">Create one to subscribe from your calendar app.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {feeds.map((feed) => {
            const httpsUrl = calendarFeedsApi.buildSubscriptionUrl(feed.token, 'https');
            const webcalUrl = calendarFeedsApi.buildSubscriptionUrl(feed.token, 'webcal');
            return (
              <Card key={feed.userCalendarFeedId}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {feed.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Districts:{' '}
                      {feed.districtFilter && feed.districtFilter.length > 0
                        ? feed.districtFilter
                            .map((id) => districtNameLookup.get(id) ?? `#${id}`)
                            .join(', ')
                        : 'all'}
                      {' • '}
                      Types:{' '}
                      {feed.dayTypeFilter && feed.dayTypeFilter.length > 0
                        ? feed.dayTypeFilter
                            .map((id) => typeNameLookup.get(id) ?? `#${id}`)
                            .join(', ')
                        : 'all'}
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 mt-2">
                      <p className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                        {httpsUrl}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    <a
                      href={webcalUrl}
                      className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                    >
                      Subscribe (webcal://)
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopy(httpsUrl)}
                      className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
                    >
                      <HiClipboard className="mr-1 h-3.5 w-3.5" />
                      Copy URL
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(feed)}
                      className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRotateToken(feed)}
                      className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 rounded"
                    >
                      <HiRefresh className="mr-1 h-3.5 w-3.5" />
                      Regenerate URL
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(feed)}
                      className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded"
                    >
                      <HiTrash className="mr-1 h-3.5 w-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit / create modal */}
      <Modal show={modalOpen} onClose={() => setModalOpen(false)} dismissible size="xl" popup>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {editingFeed ? `Edit "${editingFeed.name}"` : 'New Calendar Feed'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="feedName">Name</Label>
              <TextInput
                id="feedName"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Pittsburgh & Mt. Lebanon closures"
                required
                maxLength={100}
              />
            </div>

            <div>
              <Label>Districts (leave empty for all)</Label>
              <SearchableMultiSelect
                options={districtOptions}
                selectedIds={form.districtFilter}
                onChange={(ids) => setForm({ ...form, districtFilter: ids })}
                placeholder="All districts"
              />
            </div>

            <div>
              <Label>Day types (leave empty for all)</Label>
              <SearchableMultiSelect
                options={typeOptions}
                selectedIds={form.dayTypeFilter}
                onChange={(ids) => setForm({ ...form, dayTypeFilter: ids })}
                placeholder="All types"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
              <Button color="light" onClick={() => setModalOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingFeed ? 'Save changes' : 'Create feed'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};
